import { TypographyWithEllipsis } from '@modules/common/components/TypographyWithEllipsis';
import { hasNoLabelPredictionUnits } from '@modules/feedback/components/FeedbackCard/utils/hasNoLabelPredictionUnits';
import { FeedbackSummaryList } from '@modules/feedback/components/FeedbackSummary';
import { FlagSummaryButton } from '@modules/feedback/components/FeedbackSummary/FlagSummaryButton';
import { getFeedbackSummary } from '@modules/feedback/components/FeedbackSummary/utils';
import { useIsFeedbackOnFeedbackV2Enabled } from '@modules/feedback/hooks';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import { trackCopyFeedbackContentEvents } from '@modules/feedback/utils/trackCopyFeedbackContentEvents';
import { SummarizeRounded } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Box,
  Stack,
  Typography,
} from '@mui/material';
import { SummaryFlag } from 'generated/graphql';
import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import {
  Feedback,
  FeedbackLabel,
  FeedbackLabelType,
} from 'v2/core/entities/feedback';

interface FeedbackSummaryProps {
  feedback: Feedback;
  selectedLabels: FeedbackLabel[];
  shouldShowFeedbackSummaryActions?: boolean;
}

export const FeedbackSummary = ({
  feedback,
  selectedLabels,
  shouldShowFeedbackSummaryActions = true,
}: FeedbackSummaryProps) => {
  const summary = getFeedbackSummary(feedback);
  const [isExpanded, setIsExpanded] = useState(true);
  const { isFeedbackOnFeedbackV2Enabled } = useIsFeedbackOnFeedbackV2Enabled();
  const flaggedSummaryRecords = useFeedbackOnFeedbackStore(
    ({ flaggedSummaryRecords }) => flaggedSummaryRecords,
  );
  const flaggedRecord = flaggedSummaryRecords.find(
    (record) => record.id === feedback.id,
  );
  const isFlagged = flaggedRecord
    ? !!flaggedRecord.summaryFlagType
    : !!summary?.flag;

  const shouldHighlightSummary = useMemo(() => {
    return Boolean(
      selectedLabels.find(
        (label) =>
          (label.type === FeedbackLabelType.Reason ||
            label.type === FeedbackLabelType.Entity) &&
          hasNoLabelPredictionUnits(label),
      ),
    );
  }, [selectedLabels]);

  useEffect(() => {
    // if collapsed, expand accordion on summary highlight
    if (isExpanded || !shouldHighlightSummary) {
      return;
    }
    setIsExpanded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldHighlightSummary]);

  if (!summary?.units?.length) {
    if (!isFeedbackOnFeedbackV2Enabled) {
      return null;
    }

    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        pl={3}
        pr={2}
        py={1}
        borderRadius={2}
        sx={{
          height: '48px',
          bgcolor: 'feedback.summaryCard',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          overflow="hidden"
        >
          <SummarizeRounded
            sx={{
              color: 'text.disabled',
            }}
          />
          <TypographyWithEllipsis color="text.disabled">
            Summary unavailable: Customer feedback not detected.
          </TypographyWithEllipsis>
        </Stack>
        {shouldShowFeedbackSummaryActions && (
          <FlagSummaryButton
            feedbackID={feedback.id}
            initialFlag={summary?.flag}
            summaryFlagType={SummaryFlag.Missing}
          />
        )}
      </Stack>
    );
  }

  const onAccordionChange = (_: SyntheticEvent, expanded: boolean) => {
    setIsExpanded(expanded);
  };

  const primaryColor = shouldHighlightSummary ? 'text.primary' : 'info.main';

  return (
    <Accordion
      variant="outlined"
      expanded={isExpanded}
      onChange={onAccordionChange}
      disableGutters
      sx={{
        position: 'relative',
        bgcolor: (theme) =>
          shouldHighlightSummary
            ? alpha(theme.palette.info.main, 0.2)
            : 'feedback.summaryCard',
        '&.MuiPaper-root': {
          borderRadius: 2,
        },
      }}
    >
      {isFlagged && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: alpha('rgb(0,0,0)', 0.25),
            zIndex: 3,
          }}
        />
      )}
      <AccordionSummary>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            px={1}
            py={0.5}
          >
            <SummarizeRounded
              sx={{
                color: primaryColor,
              }}
            />
            <Typography variant="body1" color={primaryColor}>
              {isExpanded ? 'Summary' : 'View Summary'}
            </Typography>
          </Stack>
          <Stack
            sx={{
              position: 'absolute',
              right: 0,
              zIndex: 4,
              pr: 1,
            }}
          >
            <FlagSummaryButton
              feedbackID={feedback.id}
              initialFlag={summary.flag}
              summaryFlagType={SummaryFlag.Incorrect}
            />
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        sx={{ py: 0 }}
        onCopy={() => {
          trackCopyFeedbackContentEvents({
            eventName: 'Copied Text From Feedback Content',
            feedbackID: feedback.id,
            feedbackType: feedback.content.type,
            contentType: 'summary',
          });
        }}
      >
        <FeedbackSummaryList
          summaryUnits={summary.units}
          bulletIconColor={
            shouldHighlightSummary ? 'text.secondary' : undefined
          }
          shouldRenderShowAllToggle
          shouldShowAllByDefault
        />
      </AccordionDetails>
    </Accordion>
  );
};
